import type {
  CreateCheckoutSessionInput,
  GetWorkspaceBillingStateQuery,
  GetWorkspaceSubscriptionQuery,
} from '@affine/graphql';
import {
  cancelSubscriptionMutation,
  createCheckoutSessionMutation,
  getWorkspaceBillingStateQuery,
  getWorkspaceSubscriptionQuery,
  pricesQuery,
  resumeSubscriptionMutation,
  subscriptionQuery,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { GlobalCache } from '../../storage';
import type { UrlService } from '../../url';
import type { SubscriptionType as UserSubscriptionType } from '../entities/subscription';
import type { GraphQLService } from '../services/graphql';
import type { ServerService } from '../services/server';

type SubscriptionPlan = string;
export interface CancellationDeclaration {
  terminationType?: 'ordinary' | 'extraordinary';
  terminationReason?: string | null;
  language?: 'de' | 'en';
}

const SUBSCRIPTION_CACHE_KEY = 'subscription:';
const WORKSPACE_BILLING_STATE_CACHE_KEY = 'workspace-billing-state:';
const WORKSPACE_BILLING_STATE_CACHE_TTL_MS = 48 * 60 * 60 * 1000;

type WorkspaceBillingStateType =
  GetWorkspaceBillingStateQuery['workspace']['billingState'];
type WorkspaceSubscriptionCacheType =
  | NonNullable<GetWorkspaceSubscriptionQuery['workspace']['subscription']>
  | NonNullable<WorkspaceBillingStateType['billingSubscription']>;

type CachedWorkspaceBillingState = {
  cachedAt: number;
  billingState: WorkspaceBillingStateType;
};

const getDefaultSubscriptionSuccessCallbackLink = (
  baseUrl: string,
  plan?: SubscriptionPlan | null,
  scheme?: string
) => {
  const path =
    plan === 'Team'
      ? '/upgrade-success/team'
      : plan === 'Enterprise'
        ? '/upgrade-success/enterprise'
        : '/upgrade-success';
  const urlString = baseUrl + path;
  const url = new URL(urlString);
  if (scheme) {
    url.searchParams.set('scheme', scheme);
  }
  return url.toString();
};

export class SubscriptionStore extends Store {
  constructor(
    private readonly gqlService: GraphQLService,
    private readonly globalCache: GlobalCache,
    private readonly urlService: UrlService,
    private readonly serverService: ServerService
  ) {
    super();
  }

  async fetchSubscriptions(abortSignal?: AbortSignal) {
    const data = await this.gqlService.gql({
      query: subscriptionQuery,
      context: {
        signal: abortSignal,
      },
    });

    if (!data.currentUser) {
      throw new Error('No logged in');
    }

    return {
      userId: data.currentUser?.id,
      subscriptions: data.currentUser?.subscriptions,
    };
  }

  async fetchWorkspaceSubscriptions(
    workspaceId: string,
    abortSignal?: AbortSignal
  ) {
    const data = await this.gqlService.gql({
      query: getWorkspaceSubscriptionQuery,
      variables: {
        workspaceId,
      },
      context: {
        signal: abortSignal,
      },
    });

    if (!data.workspace) {
      throw new Error('No workspace');
    }

    return {
      workspaceId,
      subscription: data.workspace.subscription,
    };
  }

  async fetchWorkspaceBillingState(
    workspaceId: string,
    abortSignal?: AbortSignal
  ) {
    const data = await this.gqlService.gql({
      query: getWorkspaceBillingStateQuery,
      variables: {
        workspaceId,
      },
      context: {
        signal: abortSignal,
      },
    });

    if (!data.workspace) {
      throw new Error('No workspace');
    }

    return {
      workspaceId,
      billingState: data.workspace.billingState,
    };
  }

  async mutateResumeSubscription(
    plan?: SubscriptionPlan,
    abortSignal?: AbortSignal,
    workspaceId?: string
  ) {
    const data = await this.gqlService.gql({
      query: resumeSubscriptionMutation,
      variables: {
        plan,
        workspaceId,
      },
      context: {
        signal: abortSignal,
      },
    });
    return data.resumeSubscription;
  }

  async mutateCancelSubscription(
    plan?: SubscriptionPlan,
    abortSignal?: AbortSignal,
    workspaceId?: string,
    declaration?: CancellationDeclaration
  ) {
    const data = await this.gqlService.gql({
      query: cancelSubscriptionMutation,
      variables: {
        language: declaration?.language,
        plan,
        terminationReason: declaration?.terminationReason,
        terminationType: declaration?.terminationType,
        workspaceId,
      },
      context: {
        signal: abortSignal,
      },
    });
    return data.cancelSubscription;
  }

  getCachedSubscriptions(userId: string) {
    return this.globalCache.get<UserSubscriptionType[]>(
      SUBSCRIPTION_CACHE_KEY + userId
    );
  }

  setCachedSubscriptions(
    userId: string,
    subscriptions: UserSubscriptionType[]
  ) {
    return this.globalCache.set(SUBSCRIPTION_CACHE_KEY + userId, subscriptions);
  }

  getCachedWorkspaceSubscription(workspaceId: string) {
    return this.globalCache.get<WorkspaceSubscriptionCacheType>(
      SUBSCRIPTION_CACHE_KEY + workspaceId
    );
  }

  setCachedWorkspaceSubscription(
    workspaceId: string,
    subscription: WorkspaceSubscriptionCacheType
  ) {
    return this.globalCache.set(
      SUBSCRIPTION_CACHE_KEY + workspaceId,
      subscription
    );
  }

  getCachedWorkspaceBillingState(workspaceId: string) {
    const cached = this.globalCache.get<CachedWorkspaceBillingState>(
      WORKSPACE_BILLING_STATE_CACHE_KEY + workspaceId
    );
    if (!cached) {
      return undefined;
    }
    if (
      typeof cached.cachedAt !== 'number' ||
      Date.now() - cached.cachedAt > WORKSPACE_BILLING_STATE_CACHE_TTL_MS
    ) {
      this.globalCache.del(WORKSPACE_BILLING_STATE_CACHE_KEY + workspaceId);
      return undefined;
    }
    return cached.billingState;
  }

  setCachedWorkspaceBillingState(
    workspaceId: string,
    billingState: WorkspaceBillingStateType
  ) {
    return this.globalCache.set<CachedWorkspaceBillingState>(
      WORKSPACE_BILLING_STATE_CACHE_KEY + workspaceId,
      {
        cachedAt: Date.now(),
        billingState,
      }
    );
  }

  async createCheckoutSession(input: CreateCheckoutSessionInput) {
    const data = await this.gqlService.gql({
      query: createCheckoutSessionMutation,
      variables: {
        input: {
          ...input,
          successCallbackLink:
            input.successCallbackLink ||
            getDefaultSubscriptionSuccessCallbackLink(
              this.serverService.server.baseUrl,
              input.plan,
              this.urlService.getClientScheme()
            ),
        },
      },
    });
    return data.createCheckoutSession;
  }

  async fetchSubscriptionPrices(abortSignal?: AbortSignal) {
    const data = await this.gqlService.gql({
      query: pricesQuery,
      context: {
        signal: abortSignal,
      },
    });

    return data.prices;
  }
}
